import { QueryFactory, TransportProtocol } from '@lmt/shared-types';
import { SortDirection } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  pagingQueryParams,
  sortingQueryParams,
  toOffset,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TablePagination } from '@lmt/unicorn-shared/app/components/common/table/TablePagination';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import { PageSize } from '@lmt/unicorn-shared/app/components/common/table/types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { useMemo, useState } from 'react';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { GDTMessagesFilter } from './components/GDTMessagesFilter';
import { GDTMessagesTable } from './components/GDTMessagesTable';

import styles from './GDTMessagesPage.module.scss';
import tableStyles from '@lmt/unicorn-shared/app/components/common/table/TableTitle.module.scss';
import { TransporterState } from './components/TransporterState';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';

import { saveAsFile } from '@lmt/unicorn-shared/app/helpers/download-files';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { GDTMessageInfo, getFileName } from './components/common';
import { triggerDownloadFileDialog } from '@lmt/unicorn-shared/app/helpers/file-helper';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { DateTimeParamUnicorn } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useEventCallback } from '@mui/material';

const defaultPerPage = PageSize.Small;

export const GDTMessagesPage: React.FC = React.memo(function GDTMessagesPage() {
  const [queryParams, setQueryParams] = useQueryParamsInTab({
    ...pagingQueryParams(defaultPerPage),
    ...sortingQueryParams('id', true),
    from: DateTimeParamUnicorn,
    to: DateTimeParamUnicorn,
  });
  const [searchKey, setSearchKey] = useState(1);
  const updateFilters = useEventCallback(
    (params: Parameters<typeof setQueryParams>[0]) => {
      setQueryParams({ ...params, page: 0 });
    },
  );
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useScopedTranslation('gdt.messages');

  const queryCache = useQueryClient();

  const { data: gdtSettings } = QueryFactory.GDTSettingsQuery.useGetQuery();
  const { data: messages, isFetching } =
    QueryFactory.GDTMessageQuery.useGetMessagesQuery(
      {
        from: queryParams.from,
        to: queryParams.to,
        pagination_Offset: toOffset(
          queryParams.page,
          queryParams.perPage,
          defaultPerPage,
        ),
        pagination_Limit: queryParams.perPage,
        sorting_SortField: queryParams.sortBy ?? 'id',
        sorting_SortDirection: queryParams.desc
          ? SortDirection.Desc
          : SortDirection.Asc,
      },
      {
        placeholderData: keepPreviousData,
      },
    );

  const fileIds: string[] = useMemo(() => {
    if (messages) {
      const allFileIds: string[] = messages.data
        .filter((message) => !!message.outgoingFileId)
        .map((message) => message.outgoingFileId!);
      return [...new Set(allFileIds)];
    }
    return [];
  }, [messages]);

  const { data: documents, isFetching: isDocumentsFetching } =
    QueryFactory.FileQuery.useSearchFilesQuery({
      isAllVersions: true,
      fileIds: fileIds,
    });

  const onDelete = useEventCallback(async (messageId: number) => {
    const result = await modals.showDeletionConfirmation({
      title: i18n.t('options.confirmations.remove_title'),
      text: i18n.t('options.confirmations.remove_text'),
      okButtonText: i18n.t('options.confirmations.remove_ok_button'),
    });
    if (result) {
      try {
        await QueryFactory.GDTMessageClient.remove(messageId);
        await queryCache.invalidateQueries({
          queryKey: QueryFactory.GDTMessageQuery.getMessagesQueryKey(),
        });
      } catch (error) {
        await modals.showError({ text: convertToErrorString(error) });
      }
    }
  });

  const onOpenFile = useEventCallback(async (message: GDTMessageInfo) => {
    const blob = new Blob([message.content]);
    try {
      await saveAsFile(getFileName(message), blob);
    } catch (e) {
      console.error('GDTMessagesPage.onOpenFile', e);
      await modals.showError({ text: convertToErrorString(e) });
    }
  });

  const onDownloadFile = useEventCallback(async (message: GDTMessageInfo) => {
    try {
      const newBlob = new Blob([message.content], { type: 'text/plain' });
      triggerDownloadFileDialog(newBlob, getFileName(message));
    } catch (error) {
      await modals.showError({ text: convertToErrorString(error) });
    }
  });

  const onResetFilters = useEventCallback(() => {
    updateFilters({
      from: null,
      to: null,
    });
    setSearchKey((searchKey) => ++searchKey);
  });

  return (
    <div className={styles.pageContainer} data-test-id="table-page-container">
      <div className={styles.tableToolbar}>
        <div className={styles.filters}>
          <TableTitle title={i18n.t('title')} count={messages?.totalCount} />
          {gdtSettings?.enabled && (
            <TransporterState protocol={TransportProtocol.GDT} />
          )}
        </div>
      </div>
      <div className={styles.tableContainer}>
        <div className={styles.table}>
          <Loading
            loading={isFetching || isDocumentsFetching}
            title={i18n.t('loading')}
          >
            <GDTMessagesTable
              data={messages?.data ?? []}
              documents={documents ?? []}
              noResults={!!messages && messages.totalCount === 0}
              onDelete={onDelete}
              onSave={isElectron() ? onOpenFile : onDownloadFile}
            />
          </Loading>
        </div>
        <TablePagination
          pagination={useMemo(
            () => ({ page: queryParams.page, perPage: queryParams.perPage }),
            [queryParams.page, queryParams.perPage],
          )}
          pageLimitTitle={i18n.t('pagination_page_limit')}
          changePagination={setQueryParams}
          totalCount={messages?.totalCount ?? 0}
        />
      </div>
      <div className={styles.filtersBlockContainer}>
        <GDTMessagesFilter
          from={queryParams.from ?? null}
          onFromChanged={useEventCallback((value) => {
            updateFilters({ from: value });
          })}
          to={queryParams.to ?? null}
          onToChanged={useEventCallback((value) => {
            updateFilters({ to: value });
          })}
          onReset={onResetFilters}
          searchKey={searchKey}
        />
      </div>
    </div>
  );
});
